/**
 * store.ts — Zustand global state. Mirrors the dcc.Store list from
 * layout.py (s-wl, s-focus, s-port, s-chat, s-alerts, s-orders, ...)
 * but flattened into one store the way React apps do it.
 */
import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import type {
  User, Quote, Position, AlertRow, ChatMessage,
  OverlayId, SubpanelId, Period, Interval,
} from '@/types';

// Sensible interval band per range, so every timeframe renders a useful number
// of bars. A coarser interval than the range yields a lone candle (e.g. 1d
// range x 1wk = 1 bar); a much finer one shows only a sliver of the range after
// the server's 10k-bar cap. Clicking a RANGE snaps the interval into this band;
// picking an interval directly is left untouched. Custom intervals (not in the
// ordered list) and unbanded ranges pass through unchanged.
const INTERVAL_ORDER = ['1s','5s','15s','30s','1m','2m','5m','15m','30m','1h','2h','4h','1d','1wk','1mo'];
const PERIOD_BAND: Record<string, [string, string]> = {
  '1d':  ['1s',  '30m'], '5d':  ['1m',  '1h'],
  '1mo': ['5m',  '1d'],  '3mo': ['15m', '1d'],
  '6mo': ['30m', '1d'],  'ytd': ['1h',  '1d'],
  '1y':  ['1h',  '1wk'], '2y':  ['1d',  '1wk'],
  '5y':  ['1d',  '1mo'], 'max': ['1d',  '1mo'],
};
function clampIntervalToPeriod(period: Period, interval: Interval): Interval {
  const band = PERIOD_BAND[period];
  const cur = INTERVAL_ORDER.indexOf(interval);
  if (!band || cur === -1) return interval;   // custom interval / unbanded range
  const lo = INTERVAL_ORDER.indexOf(band[0]);
  const hi = INTERVAL_ORDER.indexOf(band[1]);
  if (cur < lo) return INTERVAL_ORDER[lo];
  if (cur > hi) return INTERVAL_ORDER[hi];
  return interval;
}

// A Plotly shape object. Loose by design (see drawings docs above).
export type ChartDrawing = Record<string, unknown>;

// localStorage-backed maps for drawings + templates. Read once at boot;
// each mutation writes back. Guarded so a corrupt stored value can never
// throw during the store's construction.
const DRAWINGS_KEY  = 'omnistrat-chart-drawings';
const TEMPLATES_KEY = 'omnistrat-chart-templates';
function readJsonMap(key: string): Record<string, ChartDrawing[]> {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object') return {};
    // Keep only well-formed arrays of objects.
    const out: Record<string, ChartDrawing[]> = {};
    for (const [k, v] of Object.entries(parsed as Record<string, unknown>)) {
      if (Array.isArray(v)) out[k] = v.filter(x => x && typeof x === 'object') as ChartDrawing[];
    }
    return out;
  } catch { return {}; }
}
function writeJsonMap(key: string, map: Record<string, ChartDrawing[]>): void {
  if (typeof window === 'undefined') return;
  try { window.localStorage.setItem(key, JSON.stringify(map)); } catch { /* quota / private mode: keep in-memory */ }
}

interface State {
  // ── Auth ────────────────────────────────────────────────────────────────
  user:  User | null;
  token: string | null;
  setUser:  (u: User | null, t: string | null) => void;
  logout:   () => void;
  hasEnt:   (entitlement: string) => boolean;

  // ── Watchlist + focus symbol ────────────────────────────────────────────
  watchlist:    string[];
  focusSymbol:  string;
  setFocus:     (sym: string) => void;
  addWatch:     (sym: string) => void;
  removeWatch:  (sym: string) => void;

  // ── Quotes (keyed by symbol) ───────────────────────────────────────────
  quotes: Record<string, Quote>;
  setQuote: (q: Quote) => void;

  // ── Chart config ───────────────────────────────────────────────────────
  period:    Period;
  interval:  Interval;
  overlays:  OverlayId[];
  panels:    SubpanelId[];
  vpSessions: 1 | 2 | 5 | 10;   // split volume profile into N session bands
  // Per-subpanel height as a fraction of the chart-pane (Plotly y-domain
  // 0..1). Each subpanel has an independent height — drag the divider
  // above any subpanel to resize just that one. The full heights map
  // persists to localStorage as JSON.
  subpanelHeights: Partial<Record<SubpanelId, number>>;
  setPeriod:   (p: Period) => void;
  setInterval: (i: Interval) => void;
  setOverlays: (o: OverlayId[]) => void;
  setPanels:   (p: SubpanelId[]) => void;
  toggleOverlay: (o: OverlayId) => void;
  togglePanel:   (p: SubpanelId) => void;
  setVpSessions: (n: 1 | 2 | 5 | 10) => void;
  setSubpanelHeight: (id: SubpanelId, h: number) => void;

  // ── Trading ────────────────────────────────────────────────────────────
  positions: Record<string, Position>;
  setPositions: (positions: Position[]) => void;

  // ── Chart order lines (bracket scaffolding) ───────────────────────────
  // Two-way bound between OrderTicket inputs and draggable Plotly shapes
  // on MainChart. A null value means "line not drawn / not part of the
  // pending order". The bracket is canonical: BUY  -> TP > entry, SL < entry
  //                                          SELL -> TP < entry, SL > entry
  entryPrice: number | null;
  tpPrice:    number | null;
  slPrice:    number | null;
  setEntry: (v: number | null) => void;
  setTp:    (v: number | null) => void;
  setSl:    (v: number | null) => void;

  // ── Chart drawings (user-drawn trendlines / paths / shapes) ───────────
  // Keyed by symbol so a trendline on AAPL never bleeds onto TSLA, and
  // persisted to localStorage so drawings survive both the 15s figure
  // rebuild AND a page reload. A Plotly shape is an open-shaped object
  // ({ type, x0, y0, x1, y1, line, path, ... }); kept loose on purpose so
  // this layer stays decoupled from Plotly's types.
  drawings: Record<string, ChartDrawing[]>;
  setDrawings:   (symbol: string, shapes: ChartDrawing[]) => void;
  clearDrawings: (symbol: string) => void;
  deleteDrawing: (symbol: string, index: number) => void;
  // Named, symbol-agnostic drawing templates. Save the current symbol's
  // drawings under a name, then apply that set onto any symbol. Persisted.
  drawingTemplates: Record<string, ChartDrawing[]>;
  saveDrawingTemplate:   (name: string, symbol: string) => void;
  applyDrawingTemplate:  (name: string, symbol: string) => void;
  deleteDrawingTemplate: (name: string) => void;

  // ── Alerts ─────────────────────────────────────────────────────────────
  alerts: AlertRow[];
  addAlert:    (a: AlertRow) => void;
  removeAlert: (id: string) => void;

  // ── AI chat ────────────────────────────────────────────────────────────
  messages:    ChatMessage[];
  isStreaming: boolean;
  addMessage:  (m: ChatMessage) => void;
  setStreaming:(v: boolean) => void;
  clearChat:   () => void;

  // ── Theme ──────────────────────────────────────────────────────────────
  // Persisted to localStorage as 'omnistrat-theme'. The `data-theme`
  // attribute on <html> is the single source of truth for CSS variables.
  theme:    ThemeId;
  setTheme: (t: ThemeId) => void;
}

export type ThemeId = 'orange' | 'green' | 'blue' | 'purple' | 'cb-safe' | 'mono';
export const THEME_PRESETS: Array<{ id: ThemeId; label: string; swatch: string; note?: string }> = [
  { id: 'orange',  label: 'Sunset Orange',     swatch: '#ff9900' },
  { id: 'green',   label: 'Terminal Green',    swatch: '#00cc66' },
  { id: 'blue',    label: 'Cyber Blue',        swatch: '#33aaff' },
  { id: 'purple',  label: 'Royal Purple',      swatch: '#cc66ff' },
  { id: 'cb-safe', label: 'Color-Blind Safe',  swatch: '#0072B2', note: 'Wong palette · works for deutan/protan/tritan' },
  { id: 'mono',    label: 'High Contrast',     swatch: '#ffffff', note: 'No red/green; uses ▲▼ for direction' },
];

const STORED_THEME = (typeof window !== 'undefined' && window.localStorage.getItem('omnistrat-theme')) as ThemeId | null;
const INITIAL_THEME: ThemeId =
  STORED_THEME && THEME_PRESETS.some(p => p.id === STORED_THEME) ? STORED_THEME : 'orange';

/**
 * Read the current accent / gain / loss colors from the active CSS vars.
 * Used by Plotly figures which need actual hex values, not CSS strings.
 */
export function readThemeColors(): { accent: string; gain: string; loss: string } {
  if (typeof window === 'undefined') return { accent: '#ff9900', gain: '#00cc66', loss: '#ff3333' };
  const cs = getComputedStyle(document.documentElement);
  return {
    accent: cs.getPropertyValue('--accent').trim() || '#ff9900',
    gain:   cs.getPropertyValue('--gain').trim()   || '#00cc66',
    loss:   cs.getPropertyValue('--loss').trim()   || '#ff3333',
  };
}

// Default watchlist matches DEFAULT_WL from layout.py, plus a handful
// of continuous-front-month futures so first-time users see the futures
// path (Yahoo delayed by default, Tradovate real-time with BYOK) work
// without having to type "=F" themselves.
const DEFAULT_WL = [
  'AAPL', 'MSFT', 'NVDA', 'GOOGL', 'AMZN',
  'META', 'TSLA', 'SPY',  'QQQ',   'JPM', 'GS', 'COIN',
  'ES=F', 'NQ=F', 'CL=F', 'GC=F',
];

export const useStore = create<State>()(
  immer((set, get) => ({
    // Auth
    user: null,
    token: null,
    setUser: (u, t) => set(s => { s.user = u; s.token = t; }),
    logout:  ()    => set(s => { s.user = null; s.token = null; }),
    hasEnt:  (e)   => (get().user?.ent ?? []).includes(e)
                      || (get().user?.ent ?? []).includes('admin:platform'),

    // Watchlist
    watchlist:   [...DEFAULT_WL],
    focusSymbol: 'AAPL',
    setFocus:    (sym) => set(s => { s.focusSymbol = sym.toUpperCase(); }),
    addWatch:    (sym) => set(s => {
      const up = sym.toUpperCase();
      if (!s.watchlist.includes(up)) s.watchlist.push(up);
    }),
    removeWatch: (sym) => set(s => {
      s.watchlist = s.watchlist.filter(x => x !== sym.toUpperCase());
    }),

    // Quotes
    quotes: {},
    setQuote: (q) => set(s => { s.quotes[q.symbol] = q; }),

    // Chart config — VP defaults ON as a main-chart overlay (right-side
    // histogram with POC/VAH/VAL lines), panels default to vol+rsi+macd.
    period:   '1y',
    interval: '1d',
    overlays: ['sma50', 'sma200', 'bb', 'don', 'vp'],
    panels:   ['vol', 'rsi', 'macd'],
    vpSessions: 1,
    subpanelHeights: (() => {
      // Default to 7% per subpanel (down from 10%) so candles get more
      // vertical real estate when 3 subpanels are active. 3 × 7% + 6%
      // margins = 27% subpanel area → main chart fills 72% of the pane
      // instead of the previous 63%. Persisted values still override.
      const defaults: Partial<Record<SubpanelId, number>> = {
        vol: 0.07, rsi: 0.07, macd: 0.07,
        atr: 0.07, obv: 0.07, stoch: 0.07,
      };
      if (typeof window === 'undefined') return defaults;
      try {
        const raw = window.localStorage.getItem('omnistrat-subpanel-heights');
        if (raw) return { ...defaults, ...JSON.parse(raw) };
      } catch { /* fall through to defaults */ }
      return defaults;
    })(),
    setPeriod:   (p) => set(s => { s.period = p; s.interval = clampIntervalToPeriod(p, s.interval); }),
    setInterval: (i) => set(s => { s.interval = i; }),
    setOverlays: (o) => set(s => { s.overlays = o; }),
    setPanels:   (p) => set(s => { s.panels = p; }),
    toggleOverlay: (o) => set(s => {
      s.overlays = s.overlays.includes(o) ? s.overlays.filter(x => x !== o) : [...s.overlays, o];
    }),
    togglePanel: (p) => set(s => {
      s.panels = s.panels.includes(p) ? s.panels.filter(x => x !== p) : [...s.panels, p];
    }),
    setVpSessions: (n) => set(s => { s.vpSessions = n; }),
    setSubpanelHeight: (id, h) => set(s => {
      const clamped = Math.max(0.04, Math.min(0.25, h));
      s.subpanelHeights[id] = clamped;
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(
          'omnistrat-subpanel-heights',
          JSON.stringify(s.subpanelHeights),
        );
      }
    }),

    // Trading
    positions: {},
    setPositions: (positions) => set(s => {
      s.positions = {};
      for (const p of positions) s.positions[p.symbol] = p;
    }),

    // Chart order lines
    entryPrice: null,
    tpPrice:    null,
    slPrice:    null,
    setEntry: (v) => set(s => { s.entryPrice = (v === null || Number.isFinite(v)) ? v : s.entryPrice; }),
    setTp:    (v) => set(s => { s.tpPrice    = (v === null || Number.isFinite(v)) ? v : s.tpPrice; }),
    setSl:    (v) => set(s => { s.slPrice    = (v === null || Number.isFinite(v)) ? v : s.slPrice; }),

    // Chart drawings — persisted per symbol.
    drawings: readJsonMap(DRAWINGS_KEY),
    setDrawings: (symbol, shapes) => set(s => {
      const sym = symbol.toUpperCase();
      if (shapes.length) s.drawings[sym] = shapes;
      else delete s.drawings[sym];                // don't persist empty arrays
      writeJsonMap(DRAWINGS_KEY, s.drawings as Record<string, ChartDrawing[]>);
    }),
    clearDrawings: (symbol) => set(s => {
      delete s.drawings[symbol.toUpperCase()];
      writeJsonMap(DRAWINGS_KEY, s.drawings as Record<string, ChartDrawing[]>);
    }),
    deleteDrawing: (symbol, index) => set(s => {
      const sym = symbol.toUpperCase();
      const list = s.drawings[sym];
      if (!Array.isArray(list) || index < 0 || index >= list.length) return;
      list.splice(index, 1);
      if (list.length === 0) delete s.drawings[sym];
      writeJsonMap(DRAWINGS_KEY, s.drawings as Record<string, ChartDrawing[]>);
    }),

    // Drawing templates — named, symbol-agnostic, persisted.
    drawingTemplates: readJsonMap(TEMPLATES_KEY),
    saveDrawingTemplate: (name, symbol) => set(s => {
      const clean = name.trim();
      if (!clean) return;
      // Deep-clone the current symbol's drawings so a later edit to the live
      // drawing doesn't mutate the saved template (and vice-versa).
      const src = s.drawings[symbol.toUpperCase()] ?? [];
      s.drawingTemplates[clean] = JSON.parse(JSON.stringify(src)) as ChartDrawing[];
      writeJsonMap(TEMPLATES_KEY, s.drawingTemplates as Record<string, ChartDrawing[]>);
    }),
    applyDrawingTemplate: (name, symbol) => set(s => {
      const tpl = s.drawingTemplates[name];
      if (!Array.isArray(tpl)) return;
      const sym = symbol.toUpperCase();
      const cloned = JSON.parse(JSON.stringify(tpl)) as ChartDrawing[];
      if (cloned.length) s.drawings[sym] = cloned;
      else delete s.drawings[sym];
      writeJsonMap(DRAWINGS_KEY, s.drawings as Record<string, ChartDrawing[]>);
    }),
    deleteDrawingTemplate: (name) => set(s => {
      delete s.drawingTemplates[name];
      writeJsonMap(TEMPLATES_KEY, s.drawingTemplates as Record<string, ChartDrawing[]>);
    }),

    // Alerts
    alerts: [],
    addAlert:    (a)  => set(s => { s.alerts.push(a); }),
    removeAlert: (id) => set(s => { s.alerts = s.alerts.filter(a => a.id !== id); }),

    // AI chat
    messages:    [],
    isStreaming: false,
    addMessage:  (m) => set(s => { s.messages.push(m); }),
    setStreaming:(v) => set(s => { s.isStreaming = v; }),
    clearChat:   ()  => set(s => { s.messages = []; }),

    // Theme
    theme: INITIAL_THEME,
    setTheme: (t) => set(s => {
      s.theme = t;
      if (typeof window !== 'undefined') {
        document.documentElement.setAttribute('data-theme', t);
        window.localStorage.setItem('omnistrat-theme', t);
      }
    }),
  })),
);

// Apply the persisted theme attribute on load so the first paint matches.
if (typeof window !== 'undefined') {
  document.documentElement.setAttribute('data-theme', INITIAL_THEME);
}
